import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import GiftRecorder from 'Pages/public/gift-flow/GiftRecorder';
import { receiverSections } from 'Pages/public/gift-content/receiverQuestions';
import { generateSectionWithAI } from 'Utils/aiQuestionGenerator';
import NetflixPlayer from 'Components/NetflixPlayer';

// Reusable "Add a personal video message" flow with a tile-hub UX.
//
// Three screens:
//   1. HUB     — info copy + a 2×2 grid of tiles (3 sections + "Other").
//                Each tile shows its completion state (Not started / X of N
//                partial / ✓ Done). A Finish button at the bottom switches
//                to the review screen.
//   2. SECTION RECORDER — opens when the user clicks a tile. Mounts
//                GiftRecorder scoped to that one section's prompts. The
//                per-section review UI is suppressed via skipReview, so as
//                soon as the user finishes the section the recorded clips
//                are stored and the user lands back on the hub. The "Other"
//                tile keeps GiftRecorder's regular review (no skipReview)
//                because the user needs an explicit "done adding" action.
//   3. REVIEW  — opens when the user clicks Finish on the hub. Shows every
//                stored clip grouped by section with inline previews and a
//                per-section "Re-record" link. Submit flattens and emits
//                onComplete(files, { fileSections }).
//
// State (in-memory only — closing the host or refreshing loses it):
//   clipsBySection: { [sectionTitle]: File[] }  // 'Other' key for custom
//
// Caller contract:
//   open / onClose          — host controls visibility.
//   onComplete(files, meta) — fires from Submit on the review screen.
//                             files is a flat array; meta.fileSections is a
//                             parallel array of section titles (or null for
//                             "Other"/custom) so the host can tag uploads.

const OTHER_KEY = 'Other';

const DEFAULT_INFO_TITLE = 'Tell your story';
const DEFAULT_INFO_SUBTITLE = 'Preserve your family history and legacy';
const DEFAULT_INFO_BODY = 'Your stories will land in your Projector along with your other Memories';

export const PersonalVideoMessageFlow = ({
  open,
  onClose,
  onBack,
  onSkip,
  onComplete,
  infoTitle = DEFAULT_INFO_TITLE,
  infoSubtitle = DEFAULT_INFO_SUBTITLE,
  infoBody = DEFAULT_INFO_BODY,
  // When true, "Finish" submits the recorded clips immediately (kicks off the
  // upload) instead of routing through the review screen. Used by the
  // normal-user signup flow.
  skipReview = false,
  // When true, mirror the browser Back button to this overlay's internal
  // sections: each forward move (hub → recording, opening a modal) gets a
  // browser-history entry, and Back unwinds one section (recording/review → hub,
  // hub → close to content view). Only the new-user signup flow opts in.
  pinBrowserBack = false,
}) => {
  // 'hub' | 'recording' | 'review'
  const [step, setStep] = useState('hub');
  // Skip confirmation popup (hub) — guards the user against leaving the
  // "Tell your story" flow for good. Skipping keeps the one-time "asked" flag
  // set, so this page won't be offered again; Back clears it (see onBack).
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  // The stable id of the section being recorded. Never changes when its
  // display title is edited, so clips stay attached across renames.
  const [activeSectionTitle, setActiveSectionTitle] = useState(null);
  // Per-section recorder snapshot keyed by stable section id. Shape:
  //   { [sectionId]: { liveQuestions: [...], slots: [{ status, file, ... }] } }
  // We persist the FULL recorder state — not just the files — so that
  // re-entering a section restores:
  //   • the slot order / additions / deletions the user made
  //   • each slot's recorded clip (when one exists) with status DONE
  //   • the empty slots they haven't recorded yet
  // The recorder emits this snapshot in onComplete's meta.snapshot and as
  // onCancel's only argument, so both Finish and Back paths preserve it.
  const [sectionSnapshots, setSectionSnapshots] = useState({});
  // Files-by-section view of the snapshots — kept as a derived computation
  // so all the count/totals/review code below works unchanged. A section's
  // clips are the slot.file entries whose status is DONE, in slot order.
  const clipsBySection = useMemo(() => {
    const out = {};
    Object.keys(sectionSnapshots).forEach((id) => {
      const snap = sectionSnapshots[id];
      const files = ((snap && snap.slots) || [])
        .filter((s) => s && s.file)
        .map((s) => s.file);
      if (files.length > 0) out[id] = files;
    });
    return out;
  }, [sectionSnapshots]);

  // Dynamic section list — the 3 preset sections + a custom "Other", all
  // editable / deletable, plus any sections the user adds. Each entry:
  //   { id, title, intro, questions, custom }
  // `questions` is the preset prompt list (empty for custom/added sections,
  // which behave like Other — the user authors their own prompts). `title`
  // is the editable display name and becomes the upload category.
  const buildInitialSections = useCallback(() => {
    const list = receiverSections.map((s) => ({
      id: s.title,
      title: s.title,
      intro: s.intro,
      questions: s.questions,
      custom: false,
    }));
    list.push({
      id: OTHER_KEY,
      title: 'Other',
      intro: 'Your own questions',
      questions: [],
      custom: true,
    });
    return list;
  }, []);
  const [sectionList, setSectionList] = useState(buildInitialSections);

  // Inline-edit state: which tile's title is being edited + the draft value.
  const [editingSectionKey, setEditingSectionKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Viewport tracking — this whole flow is styled with inline styles, which
  // always beat injected CSS classes, so media-query stylesheets can't make
  // it responsive. Instead we track the breakpoint here and branch the inline
  // style values below. `isMobile` ≤640px (phones), `isNarrow` ≤768px (small
  // tablets / large phones) — used to switch the tile grid to one column,
  // shrink the section title so the full question wraps, and keep the
  // rename/delete icons from overlapping the badge.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 640 : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getSectionById = useCallback(
    (id) => sectionList.find((s) => s.id === id) || null,
    [sectionList],
  );
  const startEditSection = useCallback((id) => {
    setEditingSectionKey(id);
    const s = sectionList.find((x) => x.id === id);
    setEditingValue(s ? s.title : '');
  }, [sectionList]);
  const cancelEditSection = useCallback(() => {
    setEditingSectionKey(null);
    setEditingValue('');
  }, []);
  const saveEditSection = useCallback(() => {
    setEditingSectionKey((id) => {
      if (id) {
        const v = editingValue.trim();
        setSectionList((prev) => prev.map((s) => (
          s.id === id ? { ...s, title: v || s.title } : s
        )));
      }
      return null;
    });
    setEditingValue('');
  }, [editingValue]);

  // Add a blank custom section (behaves like Other) and jump into renaming
  // it. Used by the modal's "Create manually" path.
  const addBlankSection = useCallback(() => {
    const id = `cmt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setSectionList((prev) => [...prev, {
      id,
      title: 'New section',
      intro: 'Start Recording',
      questions: [],
      custom: true,
    }]);
    setEditingSectionKey(id);
    setEditingValue('New section');
  }, []);

  // AI section creation modal state. Three steps: 'choose' (AI or manual),
  // 'describe' (free-form topic), 'review' (edit title + questions before
  // committing). The modal closes without writing anything if cancelled.
  const [aiModalOpen, setAIModalOpen] = useState(false);
  const [aiStep, setAIStep] = useState('choose');
  const [aiDescription, setAIDescription] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState('');
  const [aiResult, setAIResult] = useState(null); // { title, questions: string[] }
  const [aiEditingIdx, setAIEditingIdx] = useState(-1);
  const [aiEditingValue, setAIEditingValue] = useState('');

  const closeAIModal = useCallback(() => {
    setAIModalOpen(false);
    setAIStep('choose');
    setAIDescription('');
    setAILoading(false);
    setAIError('');
    setAIResult(null);
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, []);

  // Entry point for the "+ Add section" tile — opens the AI-or-manual choice
  // modal. Direct insertion still lives in addBlankSection (manual path).
  const handleAddSection = useCallback(() => {
    setAIModalOpen(true);
    setAIStep('choose');
    setAIDescription('');
    setAIError('');
    setAIResult(null);
  }, []);

  const handleAIChooseManual = useCallback(() => {
    closeAIModal();
    addBlankSection();
  }, [closeAIModal, addBlankSection]);

  const handleAIChooseAI = useCallback(() => {
    setAIStep('describe');
    setAIError('');
  }, []);

  const runAIGenerate = useCallback(async (description) => {
    setAILoading(true);
    setAIError('');
    try {
      const result = await generateSectionWithAI(description);
      setAIResult(result);
      setAIStep('review');
    } catch (e) {
      setAIError((e && e.message) || 'Generation failed.');
    } finally {
      setAILoading(false);
    }
  }, []);

  const handleAIGenerate = useCallback(() => {
    runAIGenerate(aiDescription);
  }, [runAIGenerate, aiDescription]);

  // Regenerate from the review step — silently re-uses the same description.
  const handleAIRegenerate = useCallback(() => {
    setAIEditingIdx(-1);
    setAIEditingValue('');
    runAIGenerate(aiDescription);
  }, [runAIGenerate, aiDescription]);

  const handleAIBackToDescribe = useCallback(() => {
    setAIStep('describe');
    setAIError('');
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, []);

  const handleAITitleChange = useCallback((value) => {
    setAIResult((prev) => (prev ? { ...prev, title: value } : prev));
  }, []);

  const handleAIDeleteQuestion = useCallback((idx) => {
    setAIResult((prev) => {
      if (!prev) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== idx) };
    });
    setAIEditingIdx((cur) => {
      if (cur === idx) { setAIEditingValue(''); return -1; }
      if (cur > idx) return cur - 1;
      return cur;
    });
  }, []);

  const handleAIStartEditQuestion = useCallback((idx) => {
    setAIEditingIdx(idx);
    setAIResult((prev) => {
      if (prev && prev.questions[idx] != null) setAIEditingValue(prev.questions[idx]);
      return prev;
    });
  }, []);

  const handleAISaveEditQuestion = useCallback(() => {
    setAIResult((prev) => {
      if (!prev) return prev;
      const v = aiEditingValue.trim();
      if (!v) {
        // Empty save → drop the question entirely.
        return { ...prev, questions: prev.questions.filter((_, i) => i !== aiEditingIdx) };
      }
      return {
        ...prev,
        questions: prev.questions.map((q, i) => (i === aiEditingIdx ? v : q)),
      };
    });
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, [aiEditingValue, aiEditingIdx]);

  const handleAICancelEditQuestion = useCallback(() => {
    setAIResult((prev) => {
      if (!prev) return prev;
      // If the question being cancelled was an unfinished new one (empty
      // original), drop it.
      if (aiEditingIdx >= 0 && !prev.questions[aiEditingIdx]) {
        return { ...prev, questions: prev.questions.filter((_, i) => i !== aiEditingIdx) };
      }
      return prev;
    });
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, [aiEditingIdx]);

  const handleAIAddQuestion = useCallback(() => {
    setAIResult((prev) => {
      if (!prev) return prev;
      const next = { ...prev, questions: [...prev.questions, ''] };
      setAIEditingIdx(next.questions.length - 1);
      setAIEditingValue('');
      return next;
    });
  }, []);

  // Commit the AI result as a real section in sectionList and close the
  // modal. The new section is pre-populated with its generated questions, so
  // recording mounts it via the preset (sections+skipReview) branch — same
  // as the built-in three sections.
  const handleAICommitSection = useCallback(() => {
    if (!aiResult) return;
    const title = (aiResult.title || '').trim() || 'New section';
    const questions = (aiResult.questions || [])
      .map((q) => (typeof q === 'string' ? q.trim() : ''))
      .filter(Boolean);
    if (questions.length === 0) {
      setAIError('Add at least one question before saving.');
      return;
    }
    const id = `cmt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setSectionList((prev) => [...prev, {
      id,
      title,
      intro: 'Start Recording',
      questions,
      custom: true,
    }]);
    closeAIModal();
  }, [aiResult, closeAIModal]);

  // Delete a section (and its clips). Confirms first if it has recordings.
  const handleDeleteSection = useCallback((id) => {
    const clipCount = (clipsBySection[id] || []).length;
    const name = (sectionList.find((s) => s.id === id) || {}).title || 'this section';
    // eslint-disable-next-line no-alert
    if (clipCount > 0 && !window.confirm(`Delete "${name}" and its ${clipCount} recording${clipCount === 1 ? '' : 's'}?`)) {
      return;
    }
    setSectionList((prev) => prev.filter((s) => s.id !== id));
    setSectionSnapshots((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setEditingSectionKey((k) => (k === id ? null : k));
  }, [clipsBySection, sectionList]);

  // Cache object URLs per File so video previews are stable across renders
  // and we can revoke them on unmount to avoid memory leaks.
  const urlCacheRef = useRef(new Map());

  const getPreviewUrl = useCallback((file) => {
    if (!file) return '';
    const cache = urlCacheRef.current;
    if (!cache.has(file)) cache.set(file, URL.createObjectURL(file));
    return cache.get(file);
  }, []);

  useEffect(() => () => {
    // Revoke every cached object URL when the component unmounts.
    urlCacheRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
    });
    urlCacheRef.current.clear();
  }, []);

  // Tile data with derived completion state. `key` is the stable section id;
  // `title` is the editable display title. When the user has edited a
  // section's question list (added or removed prompts), the snapshot's
  // liveQuestions length is the authoritative total — that way "1 of 2"
  // reflects what they'll actually see on re-entry, not the original
  // preset count.
  const tiles = useMemo(() => sectionList.map((s) => {
    const clipCount = (clipsBySection[s.id] || []).length;
    const snap = sectionSnapshots[s.id];
    const snapTotal = snap && Array.isArray(snap.liveQuestions)
      ? snap.liveQuestions.length
      : null;
    const presetTotal = (s.questions && s.questions.length) || 0;
    const total = snapTotal != null ? snapTotal : presetTotal;
    let state = 'notStarted';
    if (total > 0 && clipCount >= total) state = 'done';
    else if (clipCount > 0) state = 'partial';
    return {
      key: s.id,
      title: s.title,
      subtitle: s.intro,
      total: total || null,
      clipCount,
      state,
      custom: s.custom,
    };
  }), [sectionList, clipsBySection, sectionSnapshots]);

  const totalClips = useMemo(
    () => Object.values(clipsBySection).reduce((acc, arr) => acc + (arr ? arr.length : 0), 0),
    [clipsBySection],
  );

  const handleTileClick = useCallback((sectionKey) => {
    setActiveSectionTitle(sectionKey);
    setStep('recording');
  }, []);

  const handleHubClose = useCallback(() => {
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
    if (onClose) onClose();
  }, [onClose, buildInitialSections]);

  // Back (top-left) — same reset as close, but routes through onBack so the
  // host can clear the one-time "asked" flag and re-offer this page on the
  // next Next click. Falls back to onClose when no onBack is provided.
  const handleHubBack = useCallback(() => {
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
    if (onBack) onBack();
    else if (onClose) onClose();
  }, [onBack, onClose, buildInitialSections]);

  // Confirmed Skip — same reset as close, but routes through onSkip so the
  // host can set the one-time "asked" flag and retire this page.
  const handleHubSkip = useCallback(() => {
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
    if (onSkip) onSkip();
    else if (onClose) onClose();
  }, [onSkip, onClose, buildInitialSections]);

  // Persist the snapshot the recorder emitted so a follow-up entry into
  // this section restores the exact slot layout + already-recorded clips.
  // Empty snapshots (no liveQuestions AND no slots) are treated as "nothing
  // to remember" — we drop the section's entry so the next entry mounts
  // fresh.
  const persistSectionSnapshot = useCallback((sectionId, snapshot) => {
    if (!sectionId) return;
    const hasContent = !!snapshot
      && ((Array.isArray(snapshot.liveQuestions) && snapshot.liveQuestions.length > 0)
        || (Array.isArray(snapshot.slots) && snapshot.slots.length > 0));
    setSectionSnapshots((prev) => {
      if (!hasContent) {
        if (!(sectionId in prev)) return prev;
        const next = { ...prev };
        delete next[sectionId];
        return next;
      }
      return { ...prev, [sectionId]: snapshot };
    });
  }, []);

  const handleRecordingCancel = useCallback((snapshot) => {
    // Back / cancel preserves the in-progress state so the user can pick up
    // where they left off (deleted questions stay deleted, recorded slots
    // stay recorded). The recorder passes its current snapshot as the only
    // arg — older call sites that fire onCancel() with no args still work
    // because snapshot is then undefined and persistSectionSnapshot is a
    // no-op for falsy input.
    persistSectionSnapshot(activeSectionTitle, snapshot);
    setActiveSectionTitle(null);
    setStep('hub');
  }, [activeSectionTitle, persistSectionSnapshot]);

  // Every clip captured in a section recorder belongs to that section
  // (preset prompts AND any custom "+ Add Question" ones). We use the
  // emitted snapshot as the source of truth — it carries both the user's
  // current liveQuestions (after their deletions/additions) and the
  // per-slot file/status info that lets re-entry restore the exact view.
  const handleSectionRecordingComplete = useCallback((files, meta) => {
    const snapshot = meta && meta.snapshot;
    if (snapshot) {
      persistSectionSnapshot(activeSectionTitle, snapshot);
    } else if (activeSectionTitle) {
      // Legacy fallback for older code paths that emit files only — wrap
      // them into a minimal snapshot so the section card still shows the
      // recordings.
      const filesArr = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      const fallback = {
        liveQuestions: [],
        slots: filesArr.map((file) => ({ status: 'done', file })),
      };
      persistSectionSnapshot(activeSectionTitle, fallback);
    }
    setActiveSectionTitle(null);
    setStep('hub');
  }, [activeSectionTitle, persistSectionSnapshot]);

  // Submit → flatten + emit + reset. Each clip carries its section's (edited)
  // title as the upload category.
  const handleSubmit = useCallback(() => {
    if (totalClips === 0) return;
    const allFiles = [];
    const fileSections = [];
    sectionList.forEach((s) => {
      const arr = clipsBySection[s.id] || [];
      arr.forEach((file) => {
        allFiles.push(file);
        fileSections.push(s.title);
      });
    });
    if (onComplete) onComplete(allFiles, { fileSections });
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
  }, [clipsBySection, totalClips, onComplete, sectionList, buildInitialSections]);

  // Finish from hub. Normal-user flow (skipReview) submits immediately to start
  // the upload; otherwise we route through the review screen first.
  const handleFinish = useCallback(() => {
    if (totalClips === 0) return;
    if (skipReview) {
      handleSubmit();
      return;
    }
    setStep('review');
  }, [totalClips, skipReview, handleSubmit]);

  // Re-record from review → reopen that section's recorder. Overwrites the
  // section's stored clips when the user finishes (existing behavior).
  const handleReRecordFromReview = useCallback((sectionKey) => {
    setActiveSectionTitle(sectionKey);
    setStep('recording');
  }, []);

  const handleBackToHub = useCallback(() => {
    setStep('hub');
  }, []);

  // ── Make the browser Back button mirror the in-overlay Back (opt-in) ──
  // The overlay's "sections" (hub → recording/review, plus the skip / AI
  // modals) are React state on /gift-content, not routes, so the browser has no
  // history for them and a raw Back pops the whole route to the occasion page.
  // Standard SPA fix: push one history entry per FORWARD section change, and
  // unwind exactly one section on Back (popstate). On-screen Back buttons change
  // state directly, so when they move us shallower we pop the now-stale entry
  // (guarded so it doesn't re-trigger an unwind). We spread the existing React
  // Router history state into each entry so RR's {usr, key, idx} isn't clobbered.
  //
  // "Depth": 0 = closed (content view), 1 = hub, 2 = recording/review,
  // 3 = a skip / AI modal open on top of the hub.
  const modalOpen = skipConfirmOpen || aiModalOpen;
  let currentDepth = 0;
  if (open) {
    currentDepth = 1;
    if (step === 'recording' || step === 'review') currentDepth = 2;
    if (modalOpen) currentDepth = 3;
  }
  const depthRef = useRef(0);
  const fromPopRef = useRef(false);
  const ignorePopRef = useRef(false);
  useEffect(() => {
    if (!pinBrowserBack) return;
    const prev = depthRef.current;
    depthRef.current = currentDepth;
    if (currentDepth > prev) {
      // Went deeper → add a history entry the browser Back can pop.
      window.history.pushState({ ...(window.history.state || {}), pvmfLevel: currentDepth }, '');
    } else if (currentDepth < prev) {
      if (fromPopRef.current) {
        // Decrease driven by the browser Back we just handled — the entry is
        // already popped, nothing to do here.
        fromPopRef.current = false;
      } else {
        // An on-screen Back moved us shallower → pop the stale entry so the
        // browser history stays in sync. Ignore the popstate it triggers.
        ignorePopRef.current = true;
        window.history.back();
      }
    }
  }, [currentDepth, pinBrowserBack]);

  // One-step unwind, reassigned every render so the (once-registered) popstate
  // listener always runs against the current step / modal state.
  const overlayBackRef = useRef(() => {});
  overlayBackRef.current = () => {
    if (skipConfirmOpen) { setSkipConfirmOpen(false); return; }
    if (aiModalOpen) { closeAIModal(); return; }
    if (step === 'recording') { handleRecordingCancel(); return; } // → hub
    if (step === 'review') { handleBackToHub(); return; } // → hub
    handleHubBack(); // hub → close overlay back to the content view
  };
  useEffect(() => {
    if (!pinBrowserBack) return undefined;
    const onPopState = () => {
      if (ignorePopRef.current) { ignorePopRef.current = false; return; }
      if (!open) return; // already at the content view → let the natural Back run
      fromPopRef.current = true;
      overlayBackRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [open, pinBrowserBack]);

  if (!open) return null;

  // ── Inline style atoms ──
  const overlayBase = {
    position: 'fixed',
    inset: 0,
    zIndex: 9550,
    // Match the project's common gift background (gift-content + gift-flow
    // both use this radial gradient) so the hub/review screens don't look
    // like a different surface.
    background: 'radial-gradient(circle at center, #1f2330, #07080c)',
    color: '#fff',
    fontFamily: 'inherit',
    overflowY: 'auto',
  };
  const closeBtnStyle = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '36px',
    height: '36px',
    borderRadius: '999px',
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(255,255,255,0.06)',
    color: '#cfd1da',
    fontSize: '1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    zIndex: 1,
  };
  // Mirrors the /gift-content "BACK" link (gray Montserrat). Positioning is
  // owned by the top-bar flex row below so BACK and Skip share one baseline.
  const backLinkStyle = isMobile
    ? {
      cursor: 'pointer',
      color: '#888888',
      fontWeight: 500,
      fontSize: '0.7rem',
      fontFamily: "'Montserrat', sans-serif",
      border: '1px solid #707070',
      borderRadius: '5px',
      padding: '0.25rem 0.5rem',
      background: 'transparent',
      pointerEvents: 'auto',
    }
    : {
      cursor: 'pointer',
      color: '#888888',
      fontWeight: 600,
      fontSize: '18.75px',
      fontFamily: "'Montserrat', sans-serif",
      border: 'none',
      background: 'transparent',
      padding: 0,
      pointerEvents: 'auto',
    };
  const primaryBtnStyle = (disabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px 36px',
    minHeight: '48px',
    lineHeight: 1,
    borderRadius: '999px',
    border: 'none',
    background: disabled
      ? 'rgba(255,255,255,0.08)'
      : '#5aa5ef',
    color: disabled ? '#7f828b' : '#fff',
    fontFamily: 'inherit',
    fontSize: '1rem',
    fontWeight: 800,
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 8px 22px rgba(90, 165, 239, 0.40)',
  });
  const secondaryBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 18px',
    minHeight: '40px',
    lineHeight: 1,
    borderRadius: '999px',
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.06)',
    color: '#cfd1da',
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    fontWeight: 700,
    cursor: 'pointer',
  };

  const renderTile = (tile) => {
    const stateColors = {
      done: { badgeBg: 'rgba(76, 217, 100, 0.16)', badgeFg: '#bce8c2', border: 'rgba(76, 217, 100, 0.45)' },
      partial: { badgeBg: 'rgba(255, 209, 102, 0.16)', badgeFg: '#ffd98a', border: 'rgba(255, 209, 102, 0.45)' },
      notStarted: { badgeBg: 'rgba(255,255,255,0.06)', badgeFg: '#9ea2af', border: 'rgba(255,255,255,0.10)' },
    };
    const c = stateColors[tile.state];
    const tileStyle = {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      borderRadius: '16px',
      border: `1px solid ${c.border}`,
      background: 'rgba(255,255,255,0.03)',
      color: '#fff',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'transform 200ms ease, background 200ms ease, border-color 200ms ease',
      fontFamily: 'inherit',
      // Desktop tile shrunk to 120px so the hub (header + 3 rows of tiles +
      // Skip/Finish) fits inside one viewport without scroll. On phones the
      // title wraps so we keep auto height + a minHeight.
      height: isMobile ? 'auto' : '120px',
      minHeight: isMobile ? '100px' : undefined,
      width: '100%',
      boxSizing: 'border-box',
      overflow: isMobile ? 'visible' : 'hidden',
      padding: isMobile ? '12px 14px' : '14px 18px',
    };
    let badgeText;
    if (tile.state === 'done') badgeText = '✓ Done';
    else if (tile.state === 'partial' && tile.total != null) badgeText = `${tile.clipCount} of ${tile.total}`;
    else if (tile.state === 'partial') badgeText = `${tile.clipCount} clip${tile.clipCount === 1 ? '' : 's'}`;
    else badgeText = tile.total != null ? `${tile.total} prompts` : 'Add yours';

    const isEditing = editingSectionKey === tile.key;
    const smallEditBtn = {
      padding: '5px 14px',
      borderRadius: '999px',
      fontFamily: 'inherit',
      fontSize: '0.72rem',
      fontWeight: 700,
      cursor: 'pointer',
      lineHeight: 1,
    };

    if (isEditing) {
      // Edit mode — rename the section (becomes the upload category).
      // minHeight (not fixed height) + overflow visible so the compact
      // input/helper/buttons never get clipped by the tile's 150px box.
      return (
        <div
          key={tile.key}
          style={{
            ...tileStyle,
            height: 'auto',
            minHeight: '120px',
            overflow: 'visible',
            cursor: 'default',
            gap: '8px',
          }}
        >
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveEditSection(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelEditSection(); }
            }}
            aria-label="Section name"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '9px 11px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(0,0,0,0.25)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              fontWeight: 700,
            }}
          />
          <p style={{ margin: 0, color: '#9ea2af', fontSize: '0.72rem', lineHeight: 1.35 }}>
            This name becomes the category your clips are saved under.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: 'auto' }}>
            <button
              type="button"
              onClick={cancelEditSection}
              style={{
                ...smallEditBtn,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.06)',
                color: '#cfd1da',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEditSection}
              style={{
                ...smallEditBtn,
                border: 'none',
                background: '#5aa5ef',
                color: '#fff',
                fontWeight: 800,
              }}
            >
              Save
            </button>
          </div>
        </div>
      );
    }

    // View mode — a div (not <button>) acting as a button so the inline
    // rename pencil can nest next to the title without invalid button-in-
    // button markup.
    return (
      <div
        key={tile.key}
        role="button"
        tabIndex={0}
        onClick={() => handleTileClick(tile.key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTileClick(tile.key);
          }
        }}
        style={tileStyle}
      >
        {/* Header row — just title + status badge now. Edit / delete
            moved to a footer row at the bottom-right so the title can
            breathe horizontally. */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '10px',
        }}
        >
          <span style={{
            fontSize: isMobile ? '0.95rem' : '1.1rem',
            fontWeight: 800,
            letterSpacing: '-0.005em',
            lineHeight: isMobile ? 1.3 : 1.25,
            // With no icons stealing horizontal room, the title can use the
            // full row width minus the badge. Allow up to 2 lines on desktop
            // before truncating with ellipsis — keeps long section names
            // readable.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: isMobile ? 'break-word' : undefined,
            flex: 1,
            minWidth: 0,
          }}
          >
            {tile.title}
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            borderRadius: '999px',
            fontSize: '0.72rem',
            fontWeight: 800,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            background: c.badgeBg,
            color: c.badgeFg,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          >
            {badgeText}
          </span>
        </div>
        <p style={{
          margin: 0,
          color: '#b9bdc7',
          fontSize: '0.9rem',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        >
          {tile.subtitle}
        </p>
        {/* Footer row — rename / delete icons, pinned to the bottom-right
            of the card via marginTop:auto. stopPropagation so they don't
            trigger the tile's record-section action. */}
        <div style={{
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '6px',
        }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); startEditSection(tile.key); }}
            aria-label={`Rename ${tile.title}`}
            title="Rename section"
            style={{
              flexShrink: 0,
              width: '24px',
              height: '24px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(255,255,255,0.06)',
              color: '#cfd1da',
              cursor: 'pointer',
              fontSize: '0.74rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ✎
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDeleteSection(tile.key); }}
            aria-label={`Delete ${tile.title}`}
            title="Delete section"
            style={{
              flexShrink: 0,
              width: '24px',
              height: '24px',
              borderRadius: '999px',
              border: '1px solid rgba(255, 90, 90, 0.35)',
              background: 'rgba(255, 90, 90, 0.12)',
              color: '#ff9b9b',
              cursor: 'pointer',
              fontSize: '0.74rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            🗑
          </button>
        </div>
      </div>
    );
  };

  const renderReviewGroup = (label, sectionKey, clips) => {
    if (!clips.length) return null;
    return (
      <div key={sectionKey} style={{ marginBottom: '28px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '10px',
          gap: '12px',
        }}
        >
          <h2 style={{
            margin: 0, fontSize: '1rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5aa5ef',
          }}
          >
            {`${label} · ${clips.length}`}
          </h2>
          <button
            type="button"
            onClick={() => handleReRecordFromReview(sectionKey)}
            style={{
              ...secondaryBtnStyle,
              padding: '6px 14px',
              minHeight: '32px',
              fontSize: '0.78rem',
            }}
          >
            Re-record
          </button>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '12px',
        }}
        >
          {clips.map((file, i) => (
            <div
              key={`${sectionKey}-${i}`}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <NetflixPlayer
                src={getPreviewUrl(file)}
                poster={file.thumbnail || undefined}
              />
              <div style={{
                padding: '8px 10px',
                fontSize: '0.78rem',
                color: '#b9bdc7',
              }}
              >
                {`Clip ${i + 1}`}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {step === 'hub' && (
        <div
          aria-label={infoTitle}
          style={{
            ...overlayBase,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: isMobile ? '24px 16px' : '28px 24px',
          }}
        >
          {/* Top bar — BACK (left) + Skip (right) on one flex row. alignItems
              center makes their text share a single baseline, so the two are
              exactly vertically aligned regardless of the pill's box height.
              pointerEvents:none lets clicks pass through the row's empty span
              to the content below; the buttons re-enable their own clicks. */}
          <div style={{
            position: 'absolute',
            top: isMobile ? '14px' : '52px',
            left: 0,
            right: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '0 16px' : '0 60px',
            pointerEvents: 'none',
          }}
          >
            <button
              type="button"
              onClick={handleHubBack}
              aria-label="Back"
              style={backLinkStyle}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#888888'; }}
            >
              BACK
            </button>
            {/* Same Skip control as the bottom CTA, duplicated up here where
                the old ✕ close used to sit. */}
            <button
              type="button"
              onClick={() => setSkipConfirmOpen(true)}
              style={{
                pointerEvents: 'auto',
                // Web only: drop the Skip pill a touch lower than the BACK link.
                transform: isMobile ? 'none' : 'translateY(8px)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '8px 18px' : '10px 24px',
                minHeight: isMobile ? '36px' : '40px',
                lineHeight: 1,
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.06)',
                color: '#cfd1da',
                fontFamily: 'inherit',
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
          </div>
          <div style={{ width: '100%', maxWidth: '760px', marginTop: isMobile ? '48px' : 0 }}>
            <div style={{ textAlign: 'center' }}>
              {/* Reel icon + title on one line so the hero block takes less
                  vertical space and Skip/Finish stay visible without scroll. */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '4px',
              }}
              >
                <span style={{ fontSize: isMobile ? '1.9rem' : '2.2rem', lineHeight: 1 }} aria-hidden="true">🎬</span>
                <h1 style={{
                  margin: 0,
                  fontSize: isMobile ? '1.55rem' : '1.9rem',
                  fontWeight: 800,
                  letterSpacing: '-0.01em',
                }}
                >
                  {infoTitle}
                </h1>
              </div>
              {infoSubtitle && (
                <p style={{
                  margin: '6px auto 0 auto',
                  maxWidth: '520px',
                  color: '#ffd98a',
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
                >
                  {infoSubtitle}
                </p>
              )}
              <p style={{
                margin: '6px auto 18px auto',
                maxWidth: '520px',
                color: '#b9bdc7',
                fontSize: isMobile ? '0.88rem' : '0.95rem',
                lineHeight: 1.45,
              }}
              >
                {infoBody}
              </p>
            </div>

            <div style={{
              display: 'grid',
              // One column on phones so each card has the full width to show
              // the section title (question) in full + its intro line; 2-up
              // on tablets/desktop.
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: isMobile ? '8px' : '12px',
              marginBottom: '16px',
            }}
            >
              {tiles.map(renderTile)}
              {/* Add a new (custom) section. Dashed tile so it reads as an
                  affordance, not a recordable section. */}
              <button
                type="button"
                onClick={handleAddSection}
                aria-label="Add a section"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  height: isMobile ? '100px' : '120px',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: '16px',
                  border: '1px dashed rgba(255,255,255,0.25)',
                  background: 'rgba(255,255,255,0.02)',
                  color: '#cfd1da',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background 200ms ease, border-color 200ms ease',
                }}
              >
                <span style={{ fontSize: '1.5rem', lineHeight: 1 }} aria-hidden="true">＋</span>
                <span style={{ fontSize: '0.88rem', fontWeight: 700 }}>Add section</span>
              </button>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
            >
              {/* Skip lets the user bail on the whole flow without recording
                  anything. Sized to match the Finish CTA so they read as a
                  matched primary/secondary pair. */}
              <button
                type="button"
                onClick={() => setSkipConfirmOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '14px 36px',
                  minHeight: '48px',
                  lineHeight: 1,
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#cfd1da',
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleFinish}
                disabled={totalClips === 0}
                style={primaryBtnStyle(totalClips === 0)}
              >
                {totalClips === 0 ? 'Finish' : `Finish · ${totalClips} clip${totalClips === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          {/* Skip confirmation — same surface as the "Add a section" modal
              (radial card on a dark scrim). Warns the user that skipping
              retires this page; Back (which clears the flag) is the way to
              keep it. */}
          {skipConfirmOpen && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Skip recording your memories?"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9700,
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
              }}
              onClick={(e) => { if (e.target === e.currentTarget) setSkipConfirmOpen(false); }}
            >
              <div style={{
                width: '100%',
                maxWidth: '440px',
                borderRadius: '18px',
                background: 'radial-gradient(circle at center, #1f2330, #07080c)',
                border: '1px solid rgba(255,255,255,0.10)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
                color: '#fff',
                fontFamily: 'inherit',
                padding: isMobile ? '24px 20px' : '28px 26px',
                textAlign: 'center',
              }}
              >
                <div style={{ fontSize: '2rem', lineHeight: 1, marginBottom: '10px' }} aria-hidden="true">🎬</div>
                <h2 style={{
                  margin: 0,
                  fontSize: isMobile ? '1.2rem' : '1.35rem',
                  fontWeight: 800,
                  letterSpacing: '-0.01em',
                }}
                >
                  Don&apos;t you want to record your memories?
                </h2>
                <p style={{
                  margin: '10px auto 0 auto',
                  maxWidth: '360px',
                  color: '#b9bdc7',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  lineHeight: 1.45,
                }}
                >
                  These stories become part of your gift forever. If you skip now,
                  this moment to capture them won&apos;t come again.
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginTop: '22px',
                }}
                >
                  <button
                    type="button"
                    onClick={() => setSkipConfirmOpen(false)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px 26px',
                      minHeight: '46px',
                      lineHeight: 1,
                      borderRadius: '999px',
                      border: 'none',
                      background: '#5aa5ef',
                      color: '#fff',
                      fontFamily: 'inherit',
                      fontSize: '0.95rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      boxShadow: '0 8px 22px rgba(90, 165, 239, 0.40)',
                    }}
                  >
                    Keep recording
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSkipConfirmOpen(false); handleHubSkip(); }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px 26px',
                      minHeight: '46px',
                      lineHeight: 1,
                      borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cfd1da',
                      fontFamily: 'inherit',
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Skip anyway
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'review' && (
        <div
          aria-label="Review your recordings"
          style={{
            ...overlayBase,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '56px 24px',
          }}
        >
          <button type="button" onClick={handleHubClose} aria-label="Close" style={closeBtnStyle}>✕</button>
          <div style={{ width: '100%', maxWidth: '880px' }}>
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <h1 style={{
                margin: '0 0 6px 0', fontSize: '1.7rem', fontWeight: 800, letterSpacing: '-0.01em',
              }}
              >
                Review your recordings
              </h1>
              <p style={{
                margin: 0,
                color: '#b9bdc7',
                fontSize: '0.95rem',
                lineHeight: 1.5,
              }}
              >
                {`${totalClips} clip${totalClips === 1 ? '' : 's'} ready. Re-record any section, or submit when you're happy.`}
              </p>
            </div>

            <div>
              {sectionList.map((s) => (
                renderReviewGroup(s.title, s.id, clipsBySection[s.id] || [])
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '8px',
            }}
            >
              {/* On mobile the two buttons share the row equally (flex:1) with
                  matching height/padding/font so they read as the same size;
                  only their colour (outline vs gradient) differs. */}
              <button
                type="button"
                onClick={handleBackToHub}
                style={isMobile
                  ? { ...secondaryBtnStyle, flex: 1, minHeight: '48px', padding: '14px 18px', fontSize: '0.95rem' }
                  : secondaryBtnStyle}
              >
                Back to tiles
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={totalClips === 0}
                style={isMobile
                  ? { ...primaryBtnStyle(totalClips === 0), flex: 1, minHeight: '48px', padding: '14px 18px', fontSize: '0.95rem' }
                  : primaryBtnStyle(totalClips === 0)}
              >
                {`Submit · ${totalClips} clip${totalClips === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'recording' && activeSectionTitle && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9500,
            // Match the hub/review + common gift background so the recorder
            // screen isn't a different-looking surface.
            background: 'radial-gradient(circle at center, #1f2330, #07080c)',
            overflow: 'auto',
          }}
        >
          {/* Page-level Back — fixed to the viewport's top-left corner so it
              clearly reads as "leave this screen / return to the tiles"
              rather than an in-recorder control. */}
          <button
            type="button"
            onClick={handleRecordingCancel}
            aria-label="Back to sections"
            style={{
              position: 'fixed',
              top: '20px',
              left: '20px',
              zIndex: 9600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: '1.05rem', lineHeight: 1 }}>←</span>
            <span>Back</span>
          </button>
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              // On phones the recorder header ("Where it all started…") would
              // otherwise center under the fixed top-left Back button and
              // overlap it, so top-align and add clearance below the button.
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'center',
              padding: isMobile ? '72px 0 24px' : '24px 0',
              boxSizing: 'border-box',
            }}
          >
            {(() => {
              const activeSection = getSectionById(activeSectionTitle);
              if (!activeSection) {
                handleRecordingCancel();
                return null;
              }
              // Restore the user's last view of this section if we kept a
              // snapshot — preserves slot edits + recorded clips across
              // hub ↔ recorder navigation.
              const savedSnapshot = sectionSnapshots[activeSection.id];
              // Custom / added / "Other" section (no preset prompts) →
              // custom-only mode: open the add-question modal immediately
              // (autoOpenAddQuestion). skipReview is OMITTED so the user
              // gives an explicit Attach when done (no natural stop).
              if (!activeSection.questions || activeSection.questions.length === 0) {
                return (
                  <GiftRecorder
                    questions={[]}
                    recipientName=""
                    occasionLabel={activeSection.title}
                    autoOpenAddQuestion={!savedSnapshot}
                    initialState={savedSnapshot}
                    onComplete={handleSectionRecordingComplete}
                    onCancel={handleRecordingCancel}
                    showInRecorderFinish
                  />
                );
              }
              // Preset section → skipReview suppresses GiftRecorder's review
              // grid; the hub-level review handles the full preview later.
              // Pass the section (with its current/edited title) for the
              // recorder header.
              return (
                <GiftRecorder
                  questions={activeSection.questions}
                  recipientName=""
                  occasionLabel={activeSection.title}
                  initialState={savedSnapshot}
                  sections={[{
                    title: activeSection.title,
                    intro: activeSection.intro,
                    questions: activeSection.questions,
                  }]}
                  skipReview
                  onComplete={handleSectionRecordingComplete}
                  onCancel={handleRecordingCancel}
                  showInRecorderFinish
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* AI section creation modal. Rendered on top of the hub at high
          z-index so the user can cancel back to the tile grid at any step.
          Steps: 'choose' → 'describe' → 'review'. */}
      {aiModalOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add a section"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9700,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeAIModal(); }}
        >
          <div style={{
            width: '100%',
            maxWidth: '560px',
            maxHeight: 'calc(100vh - 40px)',
            overflow: 'auto',
            borderRadius: '18px',
            background: 'radial-gradient(circle at center, #1f2330, #07080c)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
            color: '#fff',
            fontFamily: 'inherit',
          }}
          >
            {/* Local keyframes + hover styles for this modal only. Scoped
                via class names that begin with `ai…` to avoid clashing. */}
            <style>
{`
              @keyframes aiSparklePulse {
                0%, 100% { transform: scale(1); opacity: 0.9; }
                50% { transform: scale(1.18); opacity: 1; }
              }
              @keyframes aiHaloSpin {
                from { transform: rotate(0deg); }
                to   { transform: rotate(360deg); }
              }
              @keyframes aiHaloPulse {
                0%, 100% { opacity: 0.55; }
                50%      { opacity: 1; }
              }
              @keyframes aiDotBounce {
                0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                40%           { transform: translateY(-6px); opacity: 1; }
              }
              @keyframes aiShimmer {
                0%   { background-position: -240px 0; }
                100% { background-position: 240px 0; }
              }
              @keyframes aiStarFloat {
                0%   { transform: translate(0, 0) scale(0.6); opacity: 0; }
                30%  { opacity: 1; }
                100% { transform: translate(var(--dx), var(--dy)) scale(1); opacity: 0; }
              }
              .aiOptionCard {
                transition: transform 180ms ease, background 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
              }
              .aiOptionCard:hover {
                transform: translateY(-2px);
                background: rgba(255,255,255,0.08) !important;
                border-color: rgba(255,255,255,0.30) !important;
                box-shadow: 0 10px 24px rgba(0,0,0,0.35);
              }
              .aiOptionCardAI:hover {
                background: linear-gradient(180deg, rgba(255,204,74,0.16), rgba(255,204,74,0.04)) !important;
                border-color: rgba(255,204,74,0.65) !important;
                box-shadow: 0 10px 24px rgba(255,204,74,0.18);
              }
              .aiGenSparkle {
                animation: aiSparklePulse 1.4s ease-in-out infinite;
              }
              .aiGenHalo {
                animation: aiHaloSpin 4s linear infinite, aiHaloPulse 2s ease-in-out infinite;
              }
              .aiGenDot {
                display: inline-block;
                width: 8px; height: 8px;
                margin: 0 4px;
                border-radius: 999px;
                background: #ffcc4a;
                animation: aiDotBounce 1.4s ease-in-out infinite both;
              }
              .aiGenDot:nth-child(1) { animation-delay: -0.32s; }
              .aiGenDot:nth-child(2) { animation-delay: -0.16s; }
              .aiGenShimmer {
                background: linear-gradient(
                  90deg,
                  rgba(255,255,255,0.55) 0%,
                  rgba(255,204,74,1) 50%,
                  rgba(255,255,255,0.55) 100%
                );
                background-size: 240px 100%;
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
                animation: aiShimmer 1.8s linear infinite;
              }
              .aiGenStar {
                position: absolute;
                top: 50%; left: 50%;
                font-size: 0.8rem;
                color: #ffcc4a;
                animation: aiStarFloat 2s ease-out infinite;
                pointer-events: none;
              }
            `}
            </style>

            {/* Header — same across all steps */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 22px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
            >
              <span style={{ fontSize: '1.05rem', fontWeight: 800 }}>
                {aiLoading && 'Generating your questions'}
                {!aiLoading && aiStep === 'choose' && 'Add a section'}
                {!aiLoading && aiStep === 'describe' && 'Describe your section'}
                {!aiLoading && aiStep === 'review' && 'Review your section'}
              </span>
              <button
                type="button"
                onClick={closeAIModal}
                aria-label="Close"
                style={{
                  width: '32px',
height: '32px',
borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.04)',
                  color: '#cfd1da',
cursor: 'pointer',
fontFamily: 'inherit',
                }}
              >
                ✕
              </button>
            </div>

            {/* Step: choose */}
            {/* Generating view — shown for both initial Generate and
                Regenerate. Replaces the form so the animation gets the full
                modal stage. */}
            {aiLoading && (
              <div style={{
                padding: '40px 24px 44px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '260px',
                position: 'relative',
                overflow: 'hidden',
              }}
              >
                {/* Sparkle + rotating halo */}
                <div style={{
                  position: 'relative',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '22px',
                }}
                >
                  {/* Halo: conic-gradient ring that spins + pulses */}
                  <div
                    aria-hidden="true"
                    className="aiGenHalo"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '999px',
                      background: 'conic-gradient(from 0deg, rgba(255,204,74,0) 0deg, rgba(255,204,74,0.85) 90deg, rgba(255,204,74,0) 200deg, rgba(255,204,74,0.45) 320deg, rgba(255,204,74,0) 360deg)',
                      filter: 'blur(2px)',
                      maskImage: 'radial-gradient(circle, transparent 38%, black 42%, black 60%, transparent 64%)',
                      WebkitMaskImage: 'radial-gradient(circle, transparent 38%, black 42%, black 60%, transparent 64%)',
                    }}
                  />
                  {/* Soft glow under the sparkle */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: '18px',
                      borderRadius: '999px',
                      background: 'radial-gradient(circle, rgba(255,204,74,0.30), rgba(255,204,74,0) 70%)',
                    }}
                  />
                  {/* Floating stars — staggered offsets via --dx/--dy. */}
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '-42px', '--dy': '-32px', animationDelay: '0s' }}>✦</span>
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '36px', '--dy': '-40px', animationDelay: '0.5s' }}>✦</span>
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '40px', '--dy': '34px', animationDelay: '1s' }}>✦</span>
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '-38px', '--dy': '36px', animationDelay: '1.5s' }}>✦</span>
                  {/* The sparkle itself — pulses gently */}
                  <span
                    aria-hidden="true"
                    className="aiGenSparkle"
                    style={{
                      position: 'relative',
                      fontSize: '2.6rem',
                      filter: 'drop-shadow(0 4px 14px rgba(255,204,74,0.55))',
                    }}
                  >
                    ✨
                  </span>
                </div>

                <div
                  className="aiGenShimmer"
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    letterSpacing: '0.01em',
                  }}
                >
                  Crafting your questions…
                </div>
                <div style={{
                  marginTop: '8px',
                  color: '#9da1ac',
                  fontSize: '0.85rem',
                  textAlign: 'center',
                  maxWidth: '380px',
                  lineHeight: 1.45,
                }}
                >
                  {aiDescription
                    ? `Thinking about “${aiDescription.length > 60 ? `${aiDescription.slice(0, 57)}…` : aiDescription}”`
                    : 'Calling on the muse'}
                </div>
                <div aria-hidden="true" style={{ marginTop: '18px', height: '12px' }}>
                  <span className="aiGenDot" />
                  <span className="aiGenDot" />
                  <span className="aiGenDot" />
                </div>
              </div>
            )}

            {aiStep === 'choose' && !aiLoading && (
              <div style={{ padding: '24px 22px 26px 22px' }}>
                <p style={{
                  margin: '0 0 20px 0',
                  color: '#cfd1da',
                  fontSize: '0.95rem',
                  textAlign: 'center',
                }}
                >
                  How would you like to create this section?
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '12px',
                }}
                >
                  <button
                    type="button"
                    onClick={handleAIChooseAI}
                    className="aiOptionCard aiOptionCardAI"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '22px 16px',
                      minHeight: '180px',
                      borderRadius: '16px',
                      border: '1px solid rgba(255,204,74,0.35)',
                      background: 'linear-gradient(180deg, rgba(255,204,74,0.08), rgba(255,204,74,0.02))',
                      color: '#fff',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'center',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="aiOptionIcon aiOptionIconAI"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '56px',
                        height: '56px',
                        borderRadius: '999px',
                        background: 'radial-gradient(circle at center, rgba(255,204,74,0.30), rgba(255,204,74,0.08))',
                        border: '1px solid rgba(255,204,74,0.40)',
                        fontSize: '1.7rem',
                      }}
                    >
                      ✨
                    </span>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '1.02rem', marginBottom: '4px' }}>
                        Generate with AI
                      </div>
                      <div style={{ color: '#a8acb6', fontSize: '0.82rem', lineHeight: 1.35 }}>
                        Describe the topic and we&apos;ll suggest questions.
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={handleAIChooseManual}
                    className="aiOptionCard"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '22px 16px',
                      minHeight: '180px',
                      borderRadius: '16px',
                      border: '1px solid rgba(255,255,255,0.16)',
                      background: 'rgba(255,255,255,0.04)',
                      color: '#fff',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'center',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="aiOptionIcon"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '56px',
                        height: '56px',
                        borderRadius: '999px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.18)',
                        fontSize: '1.6rem',
                      }}
                    >
                      ✍️
                    </span>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '1.02rem', marginBottom: '4px' }}>
                        Create manually
                      </div>
                      <div style={{ color: '#a8acb6', fontSize: '0.82rem', lineHeight: 1.35 }}>
                        Add a blank section and write your own.
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step: describe */}
            {aiStep === 'describe' && !aiLoading && (
              <div style={{ padding: '22px' }}>
                <label
                  htmlFor="ai-section-description"
                  style={{
                    display: 'block',
color: '#cfd1da',
                    fontSize: '0.9rem',
marginBottom: '8px',
                  }}
                >
                  What would you like this section to be about?
                </label>
                <textarea
                  id="ai-section-description"
                  value={aiDescription}
                  onChange={(e) => setAIDescription(e.target.value)}
                  placeholder='e.g. "My time in the army — the people I met, the lessons that stayed with me"'
                  rows={4}
                  disabled={aiLoading}
                  style={{
                    width: '100%',
boxSizing: 'border-box',
                    padding: '12px 14px',
borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.16)',
                    background: 'rgba(255,255,255,0.04)',
                    color: '#fff',
fontFamily: 'inherit',
                    fontSize: '0.95rem',
resize: 'vertical',
                    outline: 'none',
                  }}
                />
                {aiError && (
                  <div style={{
                    marginTop: '10px',
padding: '10px 12px',
                    borderRadius: '10px',
                    background: 'rgba(255,80,80,0.10)',
                    border: '1px solid rgba(255,80,80,0.25)',
                    color: '#ffb1b1',
fontSize: '0.85rem',
                  }}
                  >
                    {aiError}
                  </div>
                )}
                <div style={{
                  display: 'flex',
gap: '10px',
                  justifyContent: 'flex-end',
marginTop: '18px',
                }}
                >
                  {/* On mobile both buttons share the row equally (flex:1) with
                      matching padding/height/font so Cancel and Generate read
                      as the same size; only colour differs. */}
                  <button
                    type="button"
                    onClick={closeAIModal}
                    disabled={aiLoading}
                    style={{
                      padding: isMobile ? '12px 18px' : '10px 18px',
borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cfd1da',
cursor: 'pointer',
fontFamily: 'inherit',
                      fontSize: isMobile ? '0.95rem' : '0.9rem',
fontWeight: 700,
                      ...(isMobile ? { flex: 1, minHeight: '46px' } : {}),
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAIGenerate}
                    disabled={aiLoading || !aiDescription.trim()}
                    style={{
                      padding: isMobile ? '12px 18px' : '10px 22px',
borderRadius: '999px',
                      border: 'none',
                      background: aiLoading || !aiDescription.trim()
                        ? 'rgba(255,255,255,0.12)'
                        : '#5aa5ef',
                      color: aiLoading || !aiDescription.trim() ? '#888' : '#fff',
                      cursor: aiLoading || !aiDescription.trim() ? 'default' : 'pointer',
                      fontFamily: 'inherit',
fontSize: '0.95rem',
fontWeight: 800,
                      ...(isMobile ? { flex: 1, minHeight: '46px' } : {}),
                    }}
                  >
                    {aiLoading ? 'Generating…' : 'Generate'}
                  </button>
                </div>
              </div>
            )}

            {/* Step: review */}
            {aiStep === 'review' && aiResult && !aiLoading && (
              <div style={{ padding: '22px' }}>
                <label
                  htmlFor="ai-section-title"
                  style={{
                    display: 'block',
color: '#cfd1da',
                    fontSize: '0.85rem',
marginBottom: '6px',
                  }}
                >
                  Section title
                </label>
                <input
                  id="ai-section-title"
                  type="text"
                  value={aiResult.title}
                  onChange={(e) => handleAITitleChange(e.target.value)}
                  style={{
                    width: '100%',
boxSizing: 'border-box',
                    padding: '10px 12px',
borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.16)',
                    background: 'rgba(255,255,255,0.04)',
                    color: '#fff',
fontFamily: 'inherit',
                    fontSize: '1rem',
fontWeight: 700,
outline: 'none',
                    marginBottom: '18px',
                  }}
                />
                <div style={{
                  display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
                >
                  <span style={{ color: '#cfd1da', fontSize: '0.85rem' }}>Questions</span>
                  <span style={{ color: '#9da1ac', fontSize: '0.78rem' }}>
                    {aiResult.questions.length}
{' '}
total
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {aiResult.questions.map((q, idx) => {
                    const isEditing = aiEditingIdx === idx;
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div
key={idx}
style={{
                        display: 'flex',
alignItems: 'flex-start',
gap: '8px',
                        padding: '10px 12px',
borderRadius: '10px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                      >
                        <span style={{
                          color: '#9da1ac',
fontSize: '0.85rem',
                          fontWeight: 800,
minWidth: '20px',
paddingTop: '4px',
                        }}
                        >
                          {idx + 1}
.
                        </span>
                        {isEditing ? (
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <textarea
                              value={aiEditingValue}
                              onChange={(e) => setAIEditingValue(e.target.value)}
                              rows={2}
                              autoFocus
                              style={{
                                width: '100%',
boxSizing: 'border-box',
                                padding: '8px 10px',
borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.18)',
                                background: 'rgba(0,0,0,0.25)',
                                color: '#fff',
fontFamily: 'inherit',
                                fontSize: '0.9rem',
resize: 'vertical',
outline: 'none',
                              }}
                            />
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                onClick={handleAICancelEditQuestion}
                                style={{
                                  padding: '5px 12px',
borderRadius: '999px',
                                  border: '1px solid rgba(255,255,255,0.18)',
                                  background: 'transparent',
color: '#cfd1da',
                                  cursor: 'pointer',
fontFamily: 'inherit',
                                  fontSize: '0.72rem',
fontWeight: 700,
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleAISaveEditQuestion}
                                style={{
                                  padding: '5px 12px',
borderRadius: '999px',
                                  border: 'none',
background: '#5aa5ef',
                                  color: '#fff',
cursor: 'pointer',
                                  fontFamily: 'inherit',
fontSize: '0.72rem',
fontWeight: 800,
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span style={{ flex: 1, color: '#fff', fontSize: '0.92rem', lineHeight: 1.4 }}>
                              {q}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleAIStartEditQuestion(idx)}
                              aria-label={`Edit question ${idx + 1}`}
                              title="Edit"
                              style={{
                                background: 'transparent',
border: 'none',
                                color: '#cfd1da',
cursor: 'pointer',
                                padding: '2px 6px',
fontSize: '0.9rem',
                              }}
                            >
                              ✎
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAIDeleteQuestion(idx)}
                              aria-label={`Delete question ${idx + 1}`}
                              title="Delete"
                              style={{
                                background: 'transparent',
border: 'none',
                                color: '#ff9a9a',
cursor: 'pointer',
                                padding: '2px 6px',
fontSize: '0.9rem',
                              }}
                            >
                              🗑
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  display: 'flex',
gap: '10px',
flexWrap: 'wrap',
                  marginTop: '14px',
                }}
                >
                  <button
                    type="button"
                    onClick={handleAIAddQuestion}
                    style={{
                      padding: '8px 14px',
borderRadius: '999px',
                      border: '1px dashed rgba(255,255,255,0.25)',
                      background: 'transparent',
color: '#cfd1da',
                      cursor: 'pointer',
fontFamily: 'inherit',
                      fontSize: '0.82rem',
fontWeight: 700,
                    }}
                  >
                    ＋ Add question
                  </button>
                  {/* <button
                    type="button"
                    onClick={handleAIRegenerate}
                    disabled={aiLoading}
                    style={{
                      padding: '8px 14px',
borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cfd1da',
cursor: aiLoading ? 'default' : 'pointer',
                      fontFamily: 'inherit',
fontSize: '0.82rem',
fontWeight: 700,
                    }}
                  >
                    {aiLoading ? 'Regenerating…' : '⟲ Regenerate'}
                  </button> */}
                  <button
                    type="button"
                    onClick={handleAIBackToDescribe}
                    disabled={aiLoading}
                    style={{
                      padding: '8px 14px',
borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'transparent',
color: '#cfd1da',
                      cursor: aiLoading ? 'default' : 'pointer',
                      fontFamily: 'inherit',
fontSize: '0.82rem',
fontWeight: 700,
                    }}
                  >
                    Edit description
                  </button>
                </div>
                {aiError && (
                  <div style={{
                    marginTop: '12px',
padding: '10px 12px',
                    borderRadius: '10px',
                    background: 'rgba(255,80,80,0.10)',
                    border: '1px solid rgba(255,80,80,0.25)',
                    color: '#ffb1b1',
fontSize: '0.85rem',
                  }}
                  >
                    {aiError}
                  </div>
                )}
                <div style={{
                  display: 'flex',
gap: '10px',
                  justifyContent: 'flex-end',
marginTop: '20px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}
                >
                  <button
                    type="button"
                    onClick={closeAIModal}
                    disabled={aiLoading}
                    style={{
                      padding: '10px 18px',
borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cfd1da',
cursor: 'pointer',
fontFamily: 'inherit',
                      fontSize: '0.9rem',
fontWeight: 700,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAICommitSection}
                    disabled={aiLoading || aiResult.questions.length === 0}
                    style={{
                      padding: '10px 22px',
borderRadius: '999px',
                      border: 'none',
                      background: aiLoading || aiResult.questions.length === 0
                        ? 'rgba(255,255,255,0.12)'
                        : '#5aa5ef',
                      color: aiLoading || aiResult.questions.length === 0 ? '#888' : '#fff',
                      cursor: aiLoading || aiResult.questions.length === 0 ? 'default' : 'pointer',
                      fontFamily: 'inherit',
fontSize: '0.95rem',
fontWeight: 800,
                    }}
                  >
                    Add section
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PersonalVideoMessageFlow;
